import { ChangeDetectorRef, Directive, HostBinding, Input, OnDestroy, OnInit, inject } from '@angular/core';

import { ChatMessage } from 'src/app/cyn/chat-module/types/chat-session.types';

/** Состояние готовности блока к отображению. */
export type AgentBlockRenderState = 'idle' | 'pending' | 'ready' | 'rejected';

/**
 * Базовый класс для блоков агента с опциональной проверкой перед показом.
 * Наследник переопределяет `validateBeforeRender` / `shouldShowPendingState`
 * для кастомных проверок (ссылки, формы и т.п.).
 */
@Directive()
export abstract class AgentBlockComponent<TType extends string = string, TPayload = unknown>
    implements OnInit, OnDestroy {
    @Input() public message!: ChatMessage<TType, TPayload>;

    protected renderState: AgentBlockRenderState = 'idle';

    private readonly cdr = inject(ChangeDetectorRef);
    private destroyed = false;

    @HostBinding('attr.data-block-state')
    protected get hostRenderState(): AgentBlockRenderState {
        return this.renderState;
    }

    /** Скрываем блок до готовности и при отклонении — как будто агент его не присылал. */
    @HostBinding('hidden')
    protected get hostHidden(): boolean {
        return this.renderState === 'rejected' || this.renderState === 'idle';
    }

    public get isPending(): boolean {
        return this.renderState === 'pending';
    }

    public get isReady(): boolean {
        return this.renderState === 'ready';
    }

    public async ngOnInit(): Promise<void> {
        if (this.shouldShowPendingState()) {
            this.renderState = 'pending';
            this.cdr.markForCheck();
        }

        try {
            const ok = await this.validateBeforeRender();

            if (this.destroyed) {
                return;
            }

            this.renderState = ok ? 'ready' : 'rejected';
        } catch (error) {
            console.warn('Agent block validation failed unexpectedly', error);

            if (!this.destroyed) {
                this.renderState = 'rejected';
            }
        }

        this.cdr.markForCheck();
    }

    public ngOnDestroy(): void {
        this.destroyed = true;
    }

    /**
     * Показывать ли другой блок на время `validateBeforeRender`.
     * Например, false — если проверка не требуется или результат уже есть.
     */
    protected shouldShowPendingState(): boolean {
        return false;
    }

    /**
     * Кастомная проверка перед показом блока.
     * `false` — блок скрывается так, будто агент его не присылал.
     */
    protected async validateBeforeRender(): Promise<boolean> {
        return true;
    }
}
